import type { QuizQuestion } from "@/types/quiz";
import type { ScoringRule } from "@/types/scoring-rules";

export const SCORING_CSV_HEADER = [
  "Frage-Key",
  "Antwort-Wert",
  "Tag",
  "Gewicht",
] as const;

export const SCORING_CSV_DELIMITER_DEFAULT = ";";

// BOM (U+FEFF) als String.fromCharCode statt Escape-Sequenz, damit kein
// physisches BOM in die TS-Source rutscht.
export const SCORING_CSV_BOM = String.fromCharCode(0xfeff);

export interface ScoringCSVRow {
  questionKey: string;
  answerValue: string;
  tag: string;
  weight: number;
}

function quoteField(value: string, delimiter: string): string {
  const needsQuote =
    value.includes(delimiter) ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r");
  if (!needsQuote) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

export function serializeScoringCSV(
  rows: ScoringCSVRow[],
  delimiter: string = SCORING_CSV_DELIMITER_DEFAULT,
): string {
  const headerLine = SCORING_CSV_HEADER.join(delimiter);
  const dataLines = rows.map((row) =>
    [
      quoteField(row.questionKey, delimiter),
      quoteField(row.answerValue, delimiter),
      quoteField(row.tag, delimiter),
      String(row.weight),
    ].join(delimiter),
  );
  return SCORING_CSV_BOM + [headerLine, ...dataLines].join("\r\n");
}

export function buildScoringCSVFilename(
  quizId: string,
  today: Date = new Date(),
): string {
  const idPrefix = quizId.slice(0, 8);
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `quiznudge-scoring-${idPrefix}-${yyyy}-${mm}-${dd}.csv`;
}

export function triggerCSVDownload(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Mappt persistierte ScoringRules auf CSV-Rows. Rules ohne passende
 * Antwort in den Quiz-Fragen werden übersprungen (Drift-Schutz).
 *
 * Die Symmetrie zum Import ist beabsichtigt: Export liefert exakt die
 * vier Spalten, die der Import konsumiert. reasonText, active und
 * sortOrder werden bewusst nicht serialisiert — siehe Modul-3-Retro §4.
 */
export function rulesToCSVRows(
  rules: ScoringRule[],
  questions: QuizQuestion[],
): ScoringCSVRow[] {
  const answerMap = new Map<string, { questionKey: string; value: string }>();
  for (const q of questions) {
    for (const opt of q.options ?? []) {
      answerMap.set(opt.id, { questionKey: q.questionKey, value: opt.value });
    }
  }

  const csvRows: ScoringCSVRow[] = [];
  for (const rule of rules) {
    const answer = answerMap.get(rule.answerId);
    if (!answer) continue;
    csvRows.push({
      questionKey: answer.questionKey,
      answerValue: answer.value,
      tag: rule.targetValue,
      weight: rule.weight,
    });
  }
  return csvRows;
}
